// src/helpers/generateDoc.js
import pdfMake from "pdfmake/build/pdfmake";
import "pdfmake/build/vfs_fonts";
import logo from "../assets/images/logoDoc.png"; // URL resolved by bundler
import { toDataURL } from "./utility";

// Wire VFS (CRA/Webpack-friendly)
pdfMake.vfs = window.pdfMake.vfs;

// ---- Brand palette (adjust to your theme)
const BRAND = {
    primary: "#F97316",      // orange-500
    primarySoft: "#FFF2E8",  // soft orange bg
    ink: "#111827",          // gray-900
    subtle: "#6B7280",       // gray-500/600
    line: "#E5E7EB",         // gray-200
    tableHeader: "#F5F5F5",  // gray-100
    zebra: "#FAFAFA",        // gray-50
};

// ---- Logo preloader (no top-level await, caches result)
let _logoDataUrl = null;
let _logoPromise = null;
function getLogoDataUrl() {
    if (_logoDataUrl) return Promise.resolve(_logoDataUrl);
    if (_logoPromise) return _logoPromise;
    _logoPromise = toDataURL(logo).then((d) => (_logoDataUrl = d)).catch(() => null);
    return _logoPromise;
}

export async function generateDoc(docType, data = {}) {
    const tMap = {
        devis: "DEVIS",
        bon_commande: "BON DE COMMANDE",
        bon_livraison: "BON DE LIVRAISON",
        facture: "FACTURE",
    };

    // ---------------- Helpers ----------------
    const fmtMoney = (n) => {
        const x = Number(n || 0);
        const s = (Math.round((x + Number.EPSILON) * 100) / 100).toFixed(2);
        return s.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    };
    const fmtQty = (n) => {
        const s = String(Number(n ?? 0));
        const fixed = Number(s).toFixed(2);
        return fixed.replace(/\.00$/, "").replace(/(\.\d)0$/, "$1");
    };
    function numberToFrenchWords(n) {
        if (n === 0) return "zéro";
        const U = ["", "un", "deux", "trois", "quatre", "cinq", "six", "sept", "huit", "neuf", "dix", "onze", "douze", "treize", "quatorze", "quinze", "seize"];
        const D = ["", "dix", "vingt", "trente", "quarante", "cinquante", "soixante", "soixante", "quatre-vingt", "quatre-vingt"];
        const H = (x) => (x === 0 ? "" : x === 1 ? "cent" : U[x] + " cent" + (x > 1 ? "s" : ""));
        const under100 = (x) => {
            if (x < 17) return U[x];
            const d = Math.floor(x / 10), u = x % 10;
            if (d === 7 || d === 9) return D[d] + (u === 1 ? " et " : "-") + (u ? U[10 + u] : U[10]);
            if (d === 8 && u === 0) return "quatre-vingts";
            if (u === 1 && d >= 1 && d <= 6) return D[d] + " et un";
            return D[d] + (u ? "-" + U[u] : "");
        };
        const under1000 = (x) => {
            const c = Math.floor(x / 100), r = x % 100;
            if (!c) return under100(r);
            return r ? `${H(c)} ${under100(r)}` : H(c);
        };
        let out = "";
        const m = Math.floor(n / 1_000_000);
        const k = Math.floor((n % 1_000_000) / 1000);
        const r = n % 1000;
        if (m) out += (m === 1 ? "un million" : under1000(m) + " millions");
        if (k) out += (out ? " " : "") + (k === 1 ? "mille" : under1000(k) + " mille");
        if (r) out += (out ? " " : "") + under1000(r);
        return out;
    }
    const toFr = (d) => {
        if (!d) return "";
        const dt = new Date(d);
        return isNaN(dt) ? String(d) : dt.toLocaleDateString("fr-MA");
    };
    const amountToWordsMAD = (n) => numberToFrenchWords(Math.round(n || 0)).toUpperCase() + " DIRHAMS";

    // ---------------- INPUT EXTRACTION ----------------
    const {
        matjarDoc,
        sellerModel,
        seller,
        buyer,
        meta,
        items,
        remarks,
        tvaRate,
        logo: overrideLogo,
        priceMode = "TTC",
        currency = "MAD",
        forceTVA,
        showPrices = docType !== "bon_livraison",
        open = true,
        download = false,
    } = data;

    // ---------------- LOGO ----------------
    const logoDataUrl = overrideLogo || (await getLogoDataUrl());

    // ---------------- MATJAR MAPPING ----------------
    function mapMatjarToDoc(doc, { showPrices, forceTVA, priceMode }) {
        const wantHT = (priceMode || "TTC").toUpperCase() === "HT";

        const items = (doc.lignes || []).map((l) => {
            const unitHT = Number(l.prixUnitaireHT ?? 0);
            const unitTTC = Number(l.prixUnitaireTTC ?? 0);
            const totalHT = Number(l.montantHT);
            const totalTTC = Number(l.montantTTC);
            const sub = [l.articleCode, l.unite, l.remise ? `Remise: ${l.remise}` : null].filter(Boolean).join(" • ");

            return {
                label: l.articleNom || "",
                sublabel: sub || undefined,
                qty: Number(l.quantiteLivree ?? l.quantite ?? 0),
                rate: showPrices ? (wantHT ? unitHT : unitTTC) : 0,
                total: showPrices ? (wantHT ? totalHT : totalTTC) : 0,
                _meta: { unitHT, unitTTC, totalHT, totalTTC },
            };
        });

        const buyer = {
            name: doc.clientNom || "",
            lines: [
                doc.clientAdresse,
                doc.clientIce ? `ICE: ${doc.clientIce}` : null,
                doc.clientTelephone1 ? `Tél: ${doc.clientTelephone1}` : null,
                doc.clientEmail ? `Email: ${doc.clientEmail}` : null,
            ].filter(Boolean),
        };

        const meta = {
            number: doc.numero || "",
            date: toFr(doc.dateOperation || doc.dateCreation),
            place: doc.chantierNom && doc.chantierNom !== "Aucun" ? `Chantier : ${doc.chantierNom}` : undefined,
        };

        const remarksMerged = [
            remarks,
            doc.remarques,
            doc.validite ? `Validité: ${doc.validite} jours` : null,
            doc.dateExpiration ? `Expire le: ${toFr(doc.dateExpiration)}` : null,
            doc.modeReglement ? `Mode de règlement: ${doc.modeReglement}` : null,
            doc.statut ? `Statut: ${doc.statut}` : null,
        ]
            .filter(Boolean)
            .join("\n");

        let tvaRateCalc;
        if (typeof forceTVA === "number") tvaRateCalc = forceTVA;
        else if (Number(doc.montantTotalHT) > 0) {
            const rate = (Number(doc.montantTotalTaxe || 0) / Number(doc.montantTotalHT)) * 100;
            tvaRateCalc = Math.round(rate);
            if (!isFinite(tvaRateCalc) || tvaRateCalc < 0) tvaRateCalc = undefined;
        }

        return { buyer, meta, items, remarksMerged, tvaRateCalc, wantHT };
    }

    let mapped = {};
    if (matjarDoc) {
        mapped = mapMatjarToDoc(matjarDoc, { showPrices, forceTVA, priceMode });
    }

    const docBuyer = buyer || mapped.buyer;
    const docMeta = meta || mapped.meta;
    const docItems = items || mapped.items;
    const docRemarks = remarks || mapped.remarksMerged;
    const docTva = tvaRate ?? mapped.tvaRateCalc;
    const wantHT = priceMode === "HT" || mapped.wantHT;

    // ---------------- TOTALS ----------------
    const sum = (arr, f) => (arr || []).reduce((s, it) => s + (Number(f(it)) || 0), 0);

    const grandHT = showPrices ? sum(docItems, it => it._meta.totalHT) : 0;
    const grandTVA = showPrices ? sum(docItems, it => it._meta.totalTTC - it._meta.totalHT) : 0;
    const grandTTC = showPrices ? sum(docItems, it => it._meta.totalTTC) : 0;

    const amountText = amountToWordsMAD(grandTTC);

    // ---------------- PAYMENT STATUS (NEW) ----------------
    const montantTotalTTC = Number(matjarDoc?.montantTotalTTC ?? grandTTC);
    const montantPaye = Number(matjarDoc?.montantPaye ?? 0);
    const montantDu = montantTotalTTC - montantPaye;

    let paiementStatut = "NON PAYÉ";
    if (montantPaye >= montantTotalTTC) paiementStatut = "PAYÉ";
    else if (montantPaye > 0) paiementStatut = "PARTIELLEMENT PAYÉ";

    const statutColor =
        paiementStatut === "PAYÉ"
            ? "#16A34A"
            : paiementStatut === "PARTIELLEMENT PAYÉ"
                ? "#CA8A04"
                : "#DC2626"; // NON PAYÉ

    // ---------------- TABLE HEADER ----------------
    const headerRow = showPrices
        ? [
            { text: "#", alignment: "center", bold: true, fillColor: BRAND.tableHeader },
            { text: "Article", bold: true, fillColor: BRAND.tableHeader },
            { text: "Quantité", alignment: "center", bold: true, width: 60, fillColor: BRAND.tableHeader },
            { text: wantHT ? "P.U. HT" : docType === 'bon_livraison' ? "P.U" : "P.U. TTC", alignment: "right", bold: true, width: 70, fillColor: BRAND.tableHeader },
            { text: wantHT ? "Total HT" : docType === 'bon_livraison' ?  "Total" : "Total TTC", alignment: "right", bold: true, width: 80, fillColor: BRAND.tableHeader },
        ]
        : [
            { text: "#", alignment: "center", bold: true, fillColor: BRAND.tableHeader },
            { text: "Article", bold: true, fillColor: BRAND.tableHeader },
            { text: "Quantité", alignment: "center", bold: true, width: 60, fillColor: BRAND.tableHeader },
        ];

    const lines = docItems.map((it, i) =>
        showPrices
            ? [
                { text: String(i + 1), alignment: "center" },
                [
                    { text: it.label, bold: true },
                    it.sublabel ? { text: it.sublabel, color: BRAND.subtle, fontSize: 8 } : "",
                ],
                { text: fmtQty(it.qty), alignment: "center" },
                { text: fmtMoney(it.rate), alignment: "right" },
                { text: fmtMoney(it.total), alignment: "right" },
            ]
            : [
                { text: String(i + 1), alignment: "center" },
                [{ text: it.label, bold: true }],
                { text: fmtQty(it.qty), alignment: "center" },
            ]
    );

    const widths = showPrices ? [22, "*", 70, 70, 80] : [22, "*", 70];

    // ---------------- SELLER INFO ----------------
    const sellerInfoLines = [];

    // Address (can be multi-line)
    if (sellerModel?.address) {
        const lines = sellerModel.address.split("\n");
        lines.forEach(l => {
            sellerInfoLines.push({
                text: `${l}`,
                fontSize: 10
            });
        });
    }

    // Telephone(s)
    if (sellerModel?.phone1) {
        sellerInfoLines.push({
            text: `${sellerModel.phone1}`,
            fontSize: 9
        });
    }

    // Website / Email
    if (sellerModel?.website || sellerModel?.email) {
        sellerInfoLines.push({
            text: `${sellerModel.website || 'www.moxom.ma'}`,
            fontSize: 9,
            color: "#2563EB", // soft blue link color
        });
    }


    // ---------------- TOTALS CARD ----------------
    const totalsCard = showPrices
    ? {
        columns: [
            { width: "*", text: "" },
            {
                width: 250,
                table: {
                    widths: ["*", 100],
                    body:
                        docType === "bon_livraison"
                            ? [
                                  // Only total for BL
                                  [
                                      { text: "Total", bold: true, alignment: "right" },
                                      { text: fmtMoney(grandTTC), bold: true, alignment: "right" }
                                  ]
                              ]
                            : [
                                  // Full breakdown for other docs
                                  [
                                      { text: "Sous-total HT", alignment: "right" },
                                      { text: fmtMoney(grandHT), alignment: "right" }
                                  ],
                                  [
                                      { text: `TVA ${docTva ?? ""}%`, alignment: "right" },
                                      { text: fmtMoney(grandTVA), alignment: "right" }
                                  ],
                                  [
                                      { text: "Total TTC", bold: true, alignment: "right" },
                                      { text: fmtMoney(grandTTC), bold: true, alignment: "right" }
                                  ]
                              ]
                },
                layout: {
                    fillColor: () => BRAND.primarySoft,
                    hLineWidth: () => 0,
                    vLineWidth: () => 0,
                    paddingTop: () => 10,
                    paddingBottom: () => 10,
                    paddingLeft: () => 10,
                    paddingRight: () => 10,
                },
            },
        ],
        margin: [0, 12, 0, 0],
    }
    : null;


    // ---------------- PAYMENT CARD (NEW) ----------------
    const paymentCard = {
        margin: [0, 12, 0, 0],
        table: {
            widths: ["*"],
            body: [
                [
                    {
                        stack: [
                            {
                                columns: [
                                    { text: "STATUT DE PAIEMENT", bold: true, fontSize: 12 },
                                    {
                                        text: paiementStatut,
                                        alignment: "right",
                                        bold: true,
                                        color: statutColor,
                                    }
                                ],
                                margin: [0, 0, 0, 6]
                            },
                            {
                                columns: [
                                    { text: "Montant", color: BRAND.subtle },
                                    { text: fmtMoney(montantTotalTTC) + " MAD", alignment: "right" }
                                ]
                            },
                            {
                                columns: [
                                    { text: "Montant payé", color: BRAND.subtle },
                                    { text: fmtMoney(montantPaye) + " MAD", alignment: "right" }
                                ]
                            },
                            {
                                columns: [
                                    { text: "Montant dû", bold: true },
                                    {
                                        text: fmtMoney(montantDu) + " MAD",
                                        bold: true,
                                        alignment: "right",
                                        color: montantDu > 0 ? "#DC2626" : "#16A34A"
                                    }
                                ],
                                margin: [0, 6, 0, 0]
                            }
                        ]
                    }
                ]
            ]
        },
        layout: {
            fillColor: () => BRAND.primarySoft,
            hLineWidth: () => 0,
            vLineWidth: () => 0,
            paddingTop: () => 10,
            paddingBottom: () => 10,
            paddingLeft: () => 10,
            paddingRight: () => 10,
        }
    };

    // ---------------- PDF CONTENT ----------------
    const dd = {
        pageMargins: [10, 10, 10, 70],
        content: [
            // HEADER (Logo + Title)
            {
                columns: [
                    {
                        width: "60%",
                        stack: [
                            ...(logoDataUrl ? [{ image: logoDataUrl, width: 160, margin: [0, 0, 0, 10] }] : []),
                            ...sellerInfoLines,
                        ],
                    },
                    {
                        width: "40%",
                        stack: [
                            { text: (tMap[docType] || "DOCUMENT"), alignment: "right", fontSize: 20, bold: true },
                            { text: `#${docMeta?.number || ""}`, alignment: "right", color: BRAND.subtle },
                            {
                                margin: [0, 4, 0, 8],
                                canvas: [{ type: "rect", x: 300, y: 0, w: 200, h: 3 }],
                            },
                            {
                                text: `Date : ${docMeta?.date || ""}`,
                                alignment: "right",
                                margin: [0, 0, 0, 6],
                            },
                            {
                                table: {
                                    widths: ["*"],
                                    body: [
                                        [{ text: docBuyer?.name || "", bold: true }],
                                        ...((docBuyer?.lines || []).map(l => [{ text: l }]))
                                    ]
                                },
                                layout: "noBorders",
                                margin: [0, 6, 0, 0],
                            }
                        ]
                    }
                ],
                margin: [0, 0, 0, 14]
            },

            // PLACE
            docMeta?.place
                ? {
                    text: docMeta.place,
                    margin: [0, 0, 0, 10],
                    color: BRAND.subtle,
                }
                : null,

            // ITEMS TABLE
            {
                table: {
                    headerRows: 1,
                    widths,
                    body: [headerRow, ...lines],
                },
                layout: {
                    fillColor: (i) => (i === 0 ? BRAND.tableHeader : i % 2 === 0 ? null : BRAND.zebra),
                    hLineWidth: () => 0.6,
                    vLineWidth: () => 0,
                    hLineColor: () => BRAND.line,
                    paddingTop: () => 6,
                    paddingBottom: () => 6,
                },
                margin: [0, 6, 0, 0],
            },

            // NEW PAYMENT CARD
            paymentCard,

            // TOTALS CARD
            totalsCard,

            // AMOUNT IN WORDS
            showPrices
                ? {
                    text: `Arrêté le présent ${(tMap[docType] || "document").toLowerCase()} à la somme de : ${amountText}`,
                    margin: [0, 20, 0, 20],
                    fontSize: 9,
                    color: BRAND.subtle,
                }
                : null,
        ].filter(Boolean),

        footer: function (currentPage, pageCount) {
            return {
                margin: [20, 0, 20, 10],
                stack: [
                    { canvas: [{ type: "line", x1: 0, y1: 0, x2: 540, y2: 0, lineWidth: 1 }] },
                    {
                        text: `Page ${currentPage} / ${pageCount}`,
                        alignment: "center",
                        fontSize: 8,
                        margin: [0, 4, 0, 0],
                    },
                ],
            };
        },

        styles: {
            th: { bold: true },
            sellerLine: { fontSize: 10 },
        },
        defaultStyle: { fontSize: 10 },
    };

    const fileName = `${(tMap[docType] || "document")}_${docMeta?.number || ""}.pdf`;

    if (download) return pdfMake.createPdf(dd).download(fileName);
    if (open) return pdfMake.createPdf(dd).open();

    return dd;
}

